import React, { useMemo } from "react";
import { Tooltip } from "antd";

export interface WorkloadData {
  id: number;
  firstName: string;
  lastName: string;
  count: number;
}

interface WorkloadByAdminChartProps {
  data: WorkloadData[];
  onDrilldown: (adminId: number, firstName: string, lastName: string) => void;
}

export const WorkloadByAdminChart: React.FC<WorkloadByAdminChartProps> = ({
  data,
  onDrilldown,
}) => {
  // Sort: count desc, then alpha, but keep unassigned (id=0) at the bottom
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      // Keep unassigned (id=0) at the bottom
      if (a.id === 0) return 1;
      if (b.id === 0) return -1;

      // Sort by count descending
      if (a.count !== b.count) {
        return b.count - a.count;
      }

      // Tie-break by alphabetical order (firstName, then lastName)
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [data]);

  const maxCount = Math.max(...sortedData.map((d) => d.count));
  const avgCount =
    sortedData.reduce((sum, a) => sum + a.count, 0) / sortedData.length;

  return (
    <div
      className="bar-chart"
      style={{ maxHeight: "325px", overflowY: "auto" }}
    >
      {sortedData.map((item, idx) => {
        const adminName = `${item.firstName} ${item.lastName}`;
        const isHigh = item.count > avgCount * 1.3;
        const isLow = item.count < avgCount * 0.7;
        return (
          <Tooltip key={idx} title={adminName} placement="topLeft">
            <div
              className="bar-chart-row"
              onClick={() => {
                onDrilldown(item.id, item.firstName, item.lastName);
              }}
            >
              <div className="bar-label">{adminName}</div>
              <div className="bar-container">
                <div
                  className={`bar-fill ${
                    isHigh ? "bar-fill-warning" : isLow ? "bar-fill-info" : ""
                  }`}
                  style={{
                    width: `${(item.count / maxCount) * 100}%`,
                  }}
                />
              </div>
              <div className="bar-value">
                {item.count}
                {isHigh && " ⚠️"}
              </div>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};
